/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react-hooks/exhaustive-deps */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Card,
  CardActions,
  CardContent,
  Collapse,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useBoolean from '@main-hooks/useBoolean';
import { Alert, Button } from '@efexteam/components';
import documentsSvc from '../../../services/documents/documentsSvc';
import ExpandMore from '../ExpandMore';
import FilesList from './FileList';
import DropZone from './dropZone';
import styles from './filePicker.module.css';

interface IFilePickerProps {
  accept: string[];
  reloadFiles: () => void;
  handleFilesBulksDocument: (data: FileList | undefined) => void;
}

const FilePicker = ({
  accept,
  reloadFiles,
  handleFilesBulksDocument,
}: IFilePickerProps) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<any[]>([]);
  const [document, setDocument] = useState<FileList | undefined>(undefined);
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState(t('uploadBulkError'));
  const [uploadStarted, setUploadStarted] = useState(false);
  const [expanded, setExpanded] = useState<boolean>(true);
  const [
    isErrorMessage,
    { setTrue: showErrorMessage, setFalse: hideErrorMessage },
  ] = useBoolean(false);
  const [
    successMessage,
    { setTrue: showSuccessMessage, setFalse: hideSuccessMessage },
  ] = useBoolean(false);

  const handleOnChange = useCallback((filesNew: FileList) => {
    setFiles([]);
    setDocument(undefined);
    handleFilesBulksDocument(undefined);
    if (!filesNew) {
      return;
    }
    const singleFile = filesNew.item(0);
    if (!singleFile) return;
    const filesArray = [singleFile];

    const filesArrayData = filesArray.map((file) => ({
      id: file.name,
      file,
    }));

    setFiles(filesArrayData);
    setProgress(0);
    setUploadStarted(false);
    handleFilesBulksDocument(filesNew);
    setDocument(filesNew);
    hideErrorMessage();
    hideSuccessMessage();
  }, []);

  const handleClearFile = useCallback((id: any) => {
    setFiles((prev) => prev.filter((file) => file.id !== id));
    handleFilesBulksDocument(undefined);
    setDocument(undefined);
  }, []);

  const canShowProgress = useMemo(() => files.length > 0, [files.length]);

  const handleUpload = useCallback(async () => {
    if (!document || !document[0]) return;
    try {
      const bulks = {
        documentTypeId: 4,
        totalDocuments: 1,
        bulksDocument: document[0],
      };
      setUploadStarted(true);
      await documentsSvc.upload(bulks, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / Number(progressEvent?.total) || 0
          );
          setProgress(percentCompleted - 5);
        },
      });
      hideErrorMessage();
      showSuccessMessage();
      setUploadStarted(false);
      setFiles([]);
      handleFilesBulksDocument(undefined);
      reloadFiles();
    } catch (error: any) {
      setFiles([]);
      hideSuccessMessage();
      console.error(error);
      if (error.response && error.response.data && error.response.data.detail) {
        setErrorMessage(error.response.data.detail);
      }
      showErrorMessage();
    }
  }, [files.length, document]);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    if (files.length < 1) {
      setProgress(0);
    }
  }, [files.length]);

  useEffect(() => {
    if (progress === 100) {
      setUploadStarted(false);
    }
  }, [progress]);

  const uploadComplete = useMemo(() => progress === 100, [progress]);

  return (
    <Card className="my-3 px-2">
      <CardActions disableSpacing>
        <Typography variant="h6">{t('pyDownloadFileUpload')}</Typography>
        <ExpandMore
          expand={expanded}
          onClick={handleExpandClick}
          aria-expanded={expanded}
          aria-label="show more"
        >
          <ExpandMoreIcon />
        </ExpandMore>
      </CardActions>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent>
          <Alert
            variant="outlined"
            severity="warning"
            sx={{ maxWidth: 600, margin: '0 auto 1rem' }}
          >
            {t('ccPersonNameHelper')}
          </Alert>
          <div className={styles.wrapper}>
            <div className={styles.canvas_wrapper}>
              <DropZone onChange={handleOnChange} accept={accept} />
            </div>
            {files.length ? (
              <div className={styles.files_list_wrapper}>
                <FilesList
                  files={files}
                  onClear={handleClearFile}
                  uploadComplete={uploadComplete}
                />
              </div>
            ) : null}
            {canShowProgress || !uploadComplete ? (
              <div className={styles.files_list_progress_wrapper}>
                <progress
                  value={progress}
                  max={100}
                  style={{ width: '100%' }}
                />
              </div>
            ) : null}
            {files.length ? (
              <Button
                appearance="primary"
                onClick={() => handleUpload()}
                className={clsx(
                  uploadComplete || uploadStarted ? styles.disabled : ''
                )}
                disabled={uploadStarted || uploadComplete || !document}
              >
                {t('pyUploadFiles')}
              </Button>
            ) : null}
            {successMessage && (
              <Alert variant="outlined" severity="success">
                {t('uploadBulkSuccess')}
              </Alert>
            )}
            {isErrorMessage && (
              <Alert variant="outlined" severity="warning">
                {errorMessage}
              </Alert>
            )}
          </div>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default FilePicker;
